import { createSignal } from 'solid-js';
import { getData } from '../utils/global';
import { apiEndpoints } from '@config/env';
import { debug, warn, error as logError } from '../utils/console';
import { isLoggedIn } from './userStore';

export interface StreamingStatusSnapshot {
  /** Live map / WebSocket streaming (fast Influx poll). */
  isStreamingActive: boolean;
  /** Show LIVE dataset rows (streaming, simulation, or live processing only). */
  isLiveDatasetVisible: boolean;
  hasStreaming: boolean;
  streamingStarted: boolean;
  /** True when a historical replay simulation is running. */
  simulationActive: boolean;
  /** Live maneuver/phase script pipeline enabled (may use slower Influx poll). */
  isLiveProcessingActive: boolean;
  /** At least one InfluxDB connection is connected (false when Influx is down). */
  influxConnected: boolean;
  /** At least one source has recent data (wire field name predates the Redis retirement). */
  redisHasData: boolean;
  sourceNames: string[];
  lastUpdated: number;
}

/**
 * Global Streaming Status Store
 *
 * Maintains a global streaming status that refreshes every minute in the background.
 * Components can check this store instead of making API calls on every page load.
 */
class StreamingStatusStore {
  private statusSignal = createSignal<StreamingStatusSnapshot>({
    isStreamingActive: false,
    isLiveDatasetVisible: false,
    hasStreaming: false,
    streamingStarted: false,
    simulationActive: false,
    isLiveProcessingActive: false,
    influxConnected: true,
    redisHasData: true,
    sourceNames: [],
    lastUpdated: 0,
  });

  private refreshInterval: number | null = null;
  private retryTimer: number | null = null;
  private isRefreshing = false;
  private refreshIntervalMs = 60 * 1000; // 1 minute

  /**
   * Cap on a single status probe. /api/stream/status is a pure in-memory handler, so a slow response
   * only ever means the stream server's event loop is busy ingesting — wait briefly, then retry.
   * Without this the fetch can hang against nginx's proxy budget while LIVE rows stay hidden.
   */
  private static readonly REQUEST_TIMEOUT_MS = 8_000;
  /** Re-probe delay after a transient failure — far sooner than the 60 s heartbeat. */
  private static readonly RETRY_AFTER_FAILURE_MS = 5_000;
  /** Re-probe delay while the auth handshake is still in flight (local check, no network). */
  private static readonly RETRY_WHILE_LOGGED_OUT_MS = 3_000;
  /**
   * Fast retries are for riding out a busy stream server, not for polling a dead one. After this many
   * consecutive failures, fall back to the 60 s heartbeat so a stream server that is simply down (the
   * normal case in dev) is not probed 12x more often than before.
   */
  private static readonly MAX_FAST_RETRIES = 3;

  private consecutiveFailures = 0;

  /** Schedule a bounded fast re-probe after a transient failure. */
  private scheduleFailureRetry(): void {
    this.consecutiveFailures += 1;
    if (this.consecutiveFailures > StreamingStatusStore.MAX_FAST_RETRIES) {
      return;
    }
    this.scheduleRetry(StreamingStatusStore.RETRY_AFTER_FAILURE_MS);
  }

  /** Schedule a single near-term re-probe; existing pending retries win (no stacking). */
  private scheduleRetry(delayMs: number): void {
    if (this.retryTimer !== null) {
      return;
    }
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      void this.refreshStatus();
    }, delayMs);
  }

  /** Run a status request under REQUEST_TIMEOUT_MS. */
  private async getWithTimeout(url: string) {
    const controller = new AbortController();
    const timer = window.setTimeout(
      () => controller.abort(),
      StreamingStatusStore.REQUEST_TIMEOUT_MS
    );
    try {
      return await getData(url, controller.signal);
    } finally {
      window.clearTimeout(timer);
    }
  }

  /**
   * Initialize the store and start background refresh
   */
  initialize(): void {
    if (this.refreshInterval !== null) {
      debug('[StreamingStatusStore] Already initialized');
      return;
    }

    debug('[StreamingStatusStore] Initializing streaming status store');

    // Do an initial refresh immediately
    this.refreshStatus();

    // Set up periodic refresh. Uses the stale check so this timer stays quiet while a mounted
    // consumer (e.g. the live map status badge) is already refreshing the same snapshot faster —
    // otherwise both timers hit /api/stream/status (+ /sources) independently.
    this.refreshInterval = window.setInterval(() => {
      void this.refreshIfStale(this.refreshIntervalMs - 5_000);
    }, this.refreshIntervalMs);

    debug('[StreamingStatusStore] Started background refresh every', this.refreshIntervalMs, 'ms');
  }

  /**
   * Refresh the streaming status from the API
   */
  private async refreshStatus(): Promise<void> {
    // Prevent concurrent refreshes
    if (this.isRefreshing) {
      return;
    }

    // Skip polling when user is not authenticated - this is expected behavior.
    // This store initializes on module load, which races the startup auth handshake: without a
    // near-term retry the first successful probe would wait for the 60 s heartbeat, and LIVE dataset
    // rows stay filtered out of the Datasets table for that whole minute. The retry is a local
    // boolean check, not a request, so it costs nothing while logged out.
    if (!isLoggedIn()) {
      debug('[StreamingStatusStore] Skipping refresh - user not authenticated');
      this.scheduleRetry(StreamingStatusStore.RETRY_WHILE_LOGGED_OUT_MS);
      return;
    }

    this.isRefreshing = true;

    try {
      const statusResponse = await this.getWithTimeout(apiEndpoints.stream.status);

      // A failed probe is NOT an answer. getData resolves (rather than throws) for aborts, network
      // failures and 5xx, so without this guard a timed-out probe fell through and every field below
      // read as false — then got stamped as a fresh snapshot, hiding LIVE rows until the next tick.
      if (statusResponse?.success === false) {
        const isAuthFailure =
          statusResponse.type === 'AuthError' ||
          statusResponse.type === 'StreamAuthError' ||
          statusResponse.status === 401;

        debug(
          '[StreamingStatusStore] Stream status unavailable (auth/service):',
          statusResponse.message || statusResponse.error || statusResponse.type
        );

        if (!isAuthFailure) {
          // Service/transport failure — the stream server is busy or down, not "no live session".
          this.scheduleFailureRetry();
        }
        return;
      }

      const hasStreaming = statusResponse?.data?.hasStreaming === true;
      const streamingStarted = statusResponse?.data?.streamingStarted === true;
      const data = statusResponse?.data as Record<string, unknown> | undefined;
      const simulationActive =
        typeof data?.simulationActive === 'boolean' ? data.simulationActive : false;
      const isLiveProcessingActive =
        typeof data?.liveProcessingEnabled === 'boolean' ? data.liveProcessingEnabled : false;

      const isStreamingActive = hasStreaming && (streamingStarted || simulationActive);
      const isLiveDatasetVisible =
        streamingStarted || simulationActive || isLiveProcessingActive;

      const influxConnected =
        typeof data?.influxConnected === 'boolean' ? data.influxConnected : true;
      const redisHasData =
        typeof data?.redisHasData === 'boolean' ? data.redisHasData : true;

      let sourceNames: string[] = [];

      if (isLiveDatasetVisible && statusResponse?.success) {
        try {
          const sourcesResponse = await this.getWithTimeout(apiEndpoints.stream.sources);

          if (sourcesResponse?.success && Array.isArray(sourcesResponse.data)) {
            sourceNames = sourcesResponse.data
              .map((s: { source_name?: string }) => s.source_name)
              .filter((name: string) => name)
              .sort();
          }
        } catch (err) {
          warn('[StreamingStatusStore] Error fetching source names:', err);
        }
      }

      // A usable answer: re-arm the fast-retry budget for the next outage.
      this.consecutiveFailures = 0;

      this.statusSignal[1]({
        isStreamingActive,
        isLiveDatasetVisible,
        hasStreaming,
        streamingStarted,
        simulationActive,
        isLiveProcessingActive,
        influxConnected,
        redisHasData,
        sourceNames,
        lastUpdated: Date.now(),
      });

      import('./liveConfigStore').then(({ syncLiveConfigFromStatus }) => {
        syncLiveConfigFromStatus({
          streamingStarted,
          simulationActive,
          isLiveProcessingActive,
        });
      }).catch(() => {});
    } catch (err) {
      // Handle auth errors silently - expected when session expires or user logs out
      const errorMessage = (err as Error)?.message || String(err);
      const errorName = (err as Error)?.name || '';
      const isAuthError =
        errorName === 'AuthError' ||
        errorMessage.includes('Authentication required') ||
        errorMessage.includes('401') ||
        errorMessage.includes('No valid token') ||
        errorMessage.includes('No valid session') ||
        errorMessage.includes('No active session');

      if (isAuthError || !isLoggedIn()) {
        // Session genuinely gone: there is no live view to show, so assert the empty snapshot.
        debug('[StreamingStatusStore] Auth error during refresh (session likely expired):', errorMessage);
        this.statusSignal[1]({
          isStreamingActive: false,
          isLiveDatasetVisible: false,
          hasStreaming: false,
          streamingStarted: false,
          simulationActive: false,
          isLiveProcessingActive: false,
          influxConnected: true,
          redisHasData: true,
          sourceNames: [],
          lastUpdated: Date.now(),
        });
      } else {
        // Transient failure (timeout, or the stream server's event loop busy under live ingest).
        // Do NOT assert "no live session" and do NOT stamp lastUpdated: this used to overwrite the
        // snapshot with all-false AND mark it fresh, so refreshIfStale(55s) skipped the next tick and
        // a single slow probe hid the LIVE dataset row for a full minute. Keep the last known-good
        // snapshot and re-probe shortly instead.
        logError('[StreamingStatusStore] Error refreshing status (keeping last snapshot):', err);
        this.scheduleFailureRetry();
      }
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Get the current streaming status (reactive signal)
   */
  status() {
    return this.statusSignal[0];
  }

  /**
   * Live session active: streaming, simulation, or live processing (for LIVE datasets / sidebar).
   */
  isActive(): boolean {
    return this.statusSignal[0]().isLiveDatasetVisible;
  }

  /**
   * Get source names for active live session
   */
  getSourceNames(): string[] {
    return this.statusSignal[0]().sourceNames;
  }

  /**
   * Manually trigger a refresh (useful for immediate updates). Always hits the API — callers that
   * act on the result immediately (admin start/stop, navigation gating) depend on that.
   */
  async refresh(): Promise<void> {
    await this.refreshStatus();
  }

  /**
   * Refresh only when the snapshot is older than maxAgeMs. For periodic consumers: several
   * components poll this store on their own timers, and without a shared staleness gate each one
   * issues its own /api/stream/status (plus /api/stream/sources) request.
   */
  async refreshIfStale(maxAgeMs: number): Promise<void> {
    const lastUpdated = this.statusSignal[0]().lastUpdated;
    if (lastUpdated > 0 && Date.now() - lastUpdated < maxAgeMs) {
      return;
    }
    await this.refreshStatus();
  }

  /**
   * Cleanup - stop background refresh
   */
  cleanup(): void {
    if (this.retryTimer !== null) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.refreshInterval !== null) {
      window.clearInterval(this.refreshInterval);
      this.refreshInterval = null;
      debug('[StreamingStatusStore] Cleaned up background refresh');
    }
  }
}

// Singleton instance
export const streamingStatusStore = new StreamingStatusStore();

// Initialize on module load
streamingStatusStore.initialize();

// Cleanup on page unload (if in browser)
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    streamingStatusStore.cleanup();
  });
}
